import React, { useEffect, useRef, useState } from 'react';

import AudioPlayer from '@pray/shared/components/AudioPlayer/AudioPlayer';
import { EVENTS, LOCALES } from '@pray/shared/constants';
import contentService from '@pray/shared/services/contentService';
import trackService from '@pray/shared/services/trackService';
import eventTracking from '@pray/shared/utils/eventTracking';
import logger from '@pray/shared/utils/logger';
import { navigateToDiscoverPageAndShowContentNotFoundModal } from '@pray/shared/utils/navigation';
import nowPlaying from '@pray/shared/utils/nowPlaying';
import storage from '@pray/shared/utils/storage';

/**
 * How often the position is written down while playing, when persistence is on. A tab that
 * dies without a `pagehide` loses at most this much.
 */
const REMEMBER_EVERY_MS = 5000;

const ContentPlayer = ({
  contentItems = [],
  locale,
  itemType = 'content',
  isFetchContentData = true,
  onContentNotFound = navigateToDiscoverPageAndShowContentNotFoundModal,
  /**
   * Write what is playing to the localStorage shared with www.pray.com, so the Next.js
   * Discover can restore it after a full page load (see `utils/nowPlaying.js`). Off by
   * default: this component is also the player inside Studio and the Verizon app, where
   * nothing reads the record and nothing should be written. Only the www site turns it on.
   */
  persistNowPlaying = false,
  /**
   * Report listening progress to the server the way iOS and Android do, so what is played here
   * reaches Continue Listening on every device (see `services/trackService.ts`). Off by default
   * for the same reason as `persistNowPlaying`: this component is also the player inside Studio
   * and the Verizon app, and a leader previewing their own episode is not a listener.
   *
   * Kept separate from `persistNowPlaying` deliberately. One is a handoff between two browser
   * documents, the other is a write to the user's account, and turning one on must never be a
   * way of turning the other on by accident.
   */
  reportProgress = false,
  children,
  ...props
}) => {
  const [currentContentId, setCurrentContentId] = useState('');
  const [currentContent, setCurrentContent] = useState({});
  const [contentCache, setContentCache] = useState({});

  const [playlist, setPlaylist] = useState([]);
  const [previousContentId, setPreviousContentId] = useState();
  const [nextContentId, setNextContentId] = useState();

  const audioRef = useRef(null);
  const contentRef = useRef(null);
  const neighboursRef = useRef({});
  const rememberedAtRef = useRef(0);
  /*
   * The last listen reported to the server, so the same sitting is never reported twice.
   *
   * Two moments can describe one sitting with identical numbers: hearing a piece out fires the
   * ended report, and closing the tab a moment later computes exactly the same event. The server
   * counts each report above 95% as a completion, so the pair would put `total_completions` at 2
   * for one listen. Cleared when a new sitting begins, on play and after a seek, so a genuine
   * second listen still reports.
   */
  const lastReportedRef = useRef(null);
  /*
   * Where the sitting being reported to the server began, read from the media element.
   *
   * Kept apart from `content.startTime`, which the Amplitude event uses and which play sets from
   * the controller's state snapshot. That snapshot has not caught up when a seek lands. Listen
   * 30 seconds, drag the scrubber back to the start and leave, and the snapshot still says 30,
   * so a sitting that lasted a fifth of a second reports 29.8 seconds listened. That clears the
   * server's ten second floor and writes a started row, which zeroes that episode's completion
   * count. The floor exists to stop exactly that, so the number it judges has to be real.
   */
  const sittingStartRef = useRef(0);
  /*
   * The media element itself. `audioRef` holds the controller's snapshot of it (state values
   * that lag the element by one `timeupdate`), which is fine for the listen event and wrong
   * for a position written the instant a seek lands. The element arrives through the
   * callbacks that pass it and is preferred whenever it is known.
   */
  const elementRef = useRef(null);

  useEffect(() => {
    updateContentCache();
  }, []);

  useEffect(() => {
    setCurrentContentId(props.currentContentId);
  }, [props.currentContentId]);

  useEffect(() => {
    if (!currentContentId) {
      setCurrentContent({});
      setPlaylist([]);
      return;
    }

    if (currentContentId && currentContent.id !== currentContentId) {
      fetchContentData();
    }
  }, [currentContentId]);

  useEffect(() => {
    handleContentAccess();

    return handleContentChanged;
  }, [currentContent.id]);

  useEffect(() => {
    contentRef.current = currentContent;
  }, [currentContent]);

  useEffect(() => {
    neighboursRef.current = { previousContentId, nextContentId };
  }, [previousContentId, nextContentId]);

  /*
   * The last moment this document can still write. `pagehide` rather than `beforeunload`
   * because it fires for a navigation, a close and a tab put into the back/forward cache
   * alike, and a reader tapping the logo mid-episode is exactly the case the record is for.
   */
  useEffect(() => {
    if (!persistNowPlaying && !reportProgress) return undefined;
    const handlePageHide = () => {
      rememberNowPlaying();
      reportProgressOnPageLeave();
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [persistNowPlaying, reportProgress]);

  /*
   * Unmounting is closing: the www player is keyed on its content id and torn down by the
   * close button, and a record left behind would bring a dismissed bar back on the next page.
   * The next piece, when there is one, writes itself on its own first play.
   */
  useEffect(() => {
    return () => forgetNowPlaying();
  }, []);

  function updateContentCache() {
    const cache = contentItems.reduce((acc, item) => {
      return { ...acc, [item.id]: { ...item, cached: true } };
    }, {});
    setContentCache(cache);
  }

  async function fetchContentData() {
    let contentData = contentCache[currentContentId];

    if (contentData?.audio_url) {
      setCurrentContent(contentData);
    }

    if (isFetchContentData || !contentData || !contentData?.cached) {
      try {
        contentData = await contentService.fetchContentData({
          id: currentContentId,
          locale,
        });

        const updatedContentData = {
          ...contentData,
          ...contentCache[currentContentId],
        };

        setContentCache({
          ...contentCache,
          [currentContentId]: updatedContentData,
        });

        /**
         * Append timestamp to audio url to avoid cloudfront caching
         * This audio can be updated by the user and we want to make sure the latest audio is played
         */
        if (locale !== LOCALES.DEFAULT && updatedContentData.audio_url) {
          const audioUrl = updatedContentData.audio_url;
          try {
            const url = new URL(audioUrl);
            // Use updated_at as a stable cache key instead of Date.now()
            // This avoids generating a new URL on every fetch, which would
            // cause audio.load() to fire and reset playback position
            const cacheKey = updatedContentData.updated_at || currentContentId;
            url.searchParams.append('ts', cacheKey);
            updatedContentData.audio_url = url.href;
          } catch (err) {
            logger.error('Failed to append timestamp to audio url', err);
            updatedContentData.audio_url = audioUrl;
          }
        }

        setCurrentContent(updatedContentData);
      } catch (err) {
        const isNotFound = err.response.data?.data?.[0]?.info === 'content not found';
        if (isNotFound && onContentNotFound) onContentNotFound();
      }
    }

    updateNextContent(contentData);
  }

  function isContentGated(content) {
    if (props.isFreeAccess || !content?.id) return false;

    const user = storage.getUser();
    const isUserSubscribed = user?.is_subscribed;
    const contentIsFree = content.is_free !== false && content.is_free !== 0;
    const hasAccess = content.has_access !== false && content.has_access !== 0;

    return !isUserSubscribed && (!contentIsFree || !hasAccess);
  }

  function handleContentAccess() {
    if (isContentGated(currentContent)) {
      window.location.replace('/subscribe');
    }
  }

  /*
   * Never for gated content. The paywall path starts the audio and then reloads the document
   * onto /subscribe, and the `pagehide` write below would otherwise carry that piece across to
   * a page that has no paywall and would simply play it.
   */
  function rememberNowPlaying(position) {
    if (!persistNowPlaying) return;
    const content = contentRef.current;
    if (!content?.id || isContentGated(content)) return;

    nowPlaying.remember({
      content,
      audio: elementRef.current || audioRef.current,
      position,
      previousContentId: neighboursRef.current.previousContentId,
      nextContentId: neighboursRef.current.nextContentId,
    });
    rememberedAtRef.current = Date.now();
  }

  function forgetNowPlaying() {
    if (persistNowPlaying) nowPlaying.forget();
  }

  function handleContentChanged() {
    const audio = audioRef.current;
    if (!audio.paused) trackListenEvent();
  }

  function updateNextContent(contentData) {
    let currentIndex = playlist.findIndex((item) => item?.id === currentContentId);

    if (currentIndex === -1) {
      playlist.push(contentData);
      setPlaylist(playlist);
      currentIndex = playlist.length - 1;
    }

    const previousContent = playlist[currentIndex - 1];
    setPreviousContentId(previousContent?.id);
    setNextContentId(contentData?.next_content_id);
  }

  function handleAudioEnded(...args) {
    trackListenEvent();
    if (nextContentId) setCurrentContentId(nextContentId);
    // Heard to the end with nothing after it: there is no position to come back to.
    else forgetNowPlaying();
    if (props.onAudioEnded) props.onAudioEnded(...args);
  }

  function updateContentData(updates) {
    setCurrentContent((prevData) => ({
      ...prevData,
      ...updates,
    }));
  }

  function trackListenEvent() {
    const { amplitudeData, trackData } = getListenEventData();
    if (amplitudeData) eventTracking.trackEventListen(...amplitudeData);
    if (reportProgress && trackData && claimReport(trackData)) {
      trackService.reportListenEvent(trackData.name, trackData.properties);
    }
  }

  /**
   * Whether this listen is new, and not the one just reported.
   *
   * The Amplitude event is deliberately outside this: its volume must not change, and a repeat
   * there is only a duplicate row, while a repeat on the server is a miscount.
   */
  function claimReport(trackData) {
    const { item_id: itemId, listen_duration: listenDuration, ending_percentage: endingPercentage } = trackData.properties;
    const signature = `${itemId}|${listenDuration}|${endingPercentage}`;
    if (lastReportedRef.current === signature) return false;
    lastReportedRef.current = signature;
    return true;
  }

  /*
   * The document is going away, which is the web equivalent of the app kill that iOS reports on.
   *
   * Only the server event is sent. Firing the Amplitude one here too would add a Listen that has
   * never existed on web, inflating Listen volume and the total-listen-duration user property for
   * every listener on the site. The ticket asks for this moment and asks not to double-count, and
   * those are only compatible if the new moment belongs to the new destination alone.
   */
  function reportProgressOnPageLeave() {
    if (!reportProgress) return;
    const { trackData } = getListenEventData();
    if (trackData && claimReport(trackData)) {
      trackService.reportListenEventOnPageLeave(trackData.name, trackData.properties);
    }
  }

  /** Where the playhead is, preferring the element over the snapshot that lags it. */
  function playheadNow() {
    const element = elementRef.current;
    const audio = audioRef.current;
    const at = element && Number.isFinite(element.currentTime) ? element.currentTime : audio?.currentTime;
    return Number.isFinite(at) ? at : 0;
  }

  function getListenEventData() {
    const audio = audioRef.current;
    const content = contentRef.current;
    // `pagehide` can arrive before anything has played, when neither ref is set yet.
    if (!audio || !content) return { amplitudeData: null, trackData: null };

    // The element, not the snapshot: right after a seek the snapshot still says where it was.
    const playhead = playheadNow();
    const listenDuration = Math.abs(playhead - content.startTime);

    let amplitudeData = null;
    let trackData = null;

    // Listen only progress of the audio
    if (listenDuration >= 0.1) {
      const contentDuration = audio.duration;
      const completionRate = listenDuration / contentDuration;
      const contentSeriesTitle = content.content_series_title;
      const metricsProperties = content.metrics_properties;

      /*
       * How far through the piece the listener is: the playhead itself, clamped to 0..1, as iOS
       * reports it (`PrayMediaPlayer+Events.swift`).
       *
       * Not `startTime + |delta|`. The delta is an absolute difference, so on a backward seek the
       * two terms add instead of cancelling: starting at 100s and seeking back to 10s of a 200s
       * episode reported 0.95. Correcting it breaks this Amplitude metric against its own history
       * for backward-seek sessions, which was accepted on APP-74.
       */
      const endingPercentage = Math.min(Math.max(playhead / contentDuration, 0), 1);

      amplitudeData = [
        content.title,
        content.id,
        content.startTime,
        contentDuration,
        listenDuration,
        completionRate,
        endingPercentage,
        contentSeriesTitle,
        itemType,
        metricsProperties,
      ];

      /*
       * The sitting, measured from where it actually began rather than from the stale snapshot.
       * A scrub ends one sitting and starts another, so dragging backwards reports the fifth of
       * a second that followed it, which the server's ten second floor then correctly ignores.
       */
      const sittingStart = sittingStartRef.current;
      const sittingDuration = Math.max(playhead - sittingStart, 0);

      trackData = {
        name: EVENTS.PRAY_EVENTS.NAMES.LISTEN,
        properties: {
          title: content.title,
          item_id: content.id,
          item_type: itemType,
          series_title: contentSeriesTitle,
          start_time: sittingStart,
          content_duration: contentDuration,
          listen_duration: sittingDuration,
          completion_rate: Math.min(sittingDuration / contentDuration, 1),
          ending_percentage: endingPercentage,
        },
      };
    }

    return {
      amplitudeData,
      trackData,
    };
  }

  function setStartTime(startTime) {
    const content = contentRef.current;
    if (content) content.startTime = startTime;
  }

  function handlePlay(element) {
    const audio = audioRef.current;
    // A new sitting: whatever was reported before describes a listen that has now ended.
    lastReportedRef.current = null;
    if (element) elementRef.current = element;
    sittingStartRef.current = playheadNow();
    setStartTime(audio.currentTime);
    rememberNowPlaying((element || audio).currentTime);
  }

  function handlePause() {
    trackListenEvent();
    rememberNowPlaying();
  }

  function handleTimeUpdated(element) {
    elementRef.current = element;
    /*
     * The playhead is behind where this sitting began, so the sitting began here instead.
     *
     * Two ways that happens. A replay of a piece that has ended: the play handler runs after
     * `play()` resolves, and if the element still reads its duration at that instant the sitting
     * would start at the end, every report would measure zero, and the whole re-listen would
     * write nothing. And any seek that did not reach the skip handler. Catching it on the tick
     * that observes the move keeps the measurement, where clamping at report time would only
     * flatten it to zero.
     */
    if (Number.isFinite(element?.currentTime) && element.currentTime < sittingStartRef.current) {
      sittingStartRef.current = element.currentTime;
    }
    // The same move ends the Amplitude sitting too, so its duration and its ending agree.
    if (Number.isFinite(element?.currentTime) && contentRef.current && element.currentTime < contentRef.current.startTime) {
      setStartTime(element.currentTime);
    }
    if (element.paused || Date.now() - rememberedAtRef.current < REMEMBER_EVERY_MS) return;
    rememberNowPlaying(element.currentTime);
  }

  function handleDragStarted() {
    trackListenEvent();
  }

  function handleBeforeSkip() {
    const audio = audioRef.current;
    const isPlaying = !audio.paused && audio.currentTime > 0 && !audio.ended;
    if (isPlaying) trackListenEvent();
  }

  function handleAfterSkip() {
    const audio = audioRef.current;
    lastReportedRef.current = null;
    // The element, not the snapshot: this is where the seek actually landed.
    sittingStartRef.current = playheadNow();
    setStartTime(playheadNow());
    // The element, where a seek has already landed; the snapshot still says where it was.
    rememberNowPlaying((elementRef.current || audio).currentTime);
  }

  return (
    <AudioPlayer
      {...props}
      url={currentContent.audio_url}
      onPlay={handlePlay}
      onPause={handlePause}
      onBeforeSkip={handleBeforeSkip}
      onAfterSkip={handleAfterSkip}
      onAudioEnded={handleAudioEnded}
      onDragStarted={handleDragStarted}
      onTimeUpdated={handleTimeUpdated}
    >
      {(audioController) => {
        const contentController = {
          ...audioController,
          content: {
            ...currentContent,
            previousContentId,
            nextContentId,
          },
          updateContentData,
        };

        audioRef.current = audioController.audio;

        return children && children(contentController);
      }}
    </AudioPlayer>
  );
};

export default ContentPlayer;
